import { Component, Input, OnInit, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { webPermisosSignal } from "app/services/auth/web-permisos.signal";

import { ImagesService } from "app/services/helper/images.service";
import { ProjectService } from "app/services/project/project.service";
import { UserService } from "app/services/user/user.service";
import { ProfileUser } from "app/shared/models/user.interface";

import { VacacionesService } from "app/services/vacaciones/vacaciones.service";

import { AuthService } from "app/services/auth/auth.service";
import moment from "app/util/dayjs";

import { TipoIncidenciaAditionalInfoDialogComponent } from "./tipo-incidencia/tipo-incidencia-aditional-info-dialog.component";

interface Tipo {
    value: string;
    viewValue: string;
}

/** Fila de subtype devuelta por la API (incidents_types_subtype) */
interface IncidentsSubtypeRow {
    id: number;
    name: string;
    enabled?: number;
    otherRestriction?: string | null;
    otherPreaviso?: number | null;
    aditionalInfo?: string | null;
}

interface TipoIncidenciaExxita {
    id: number;
    tipo: string;
    /** incidents_types.optionalDocuments: máx. otros adjuntos; 0 = no mostrar sección */
    optionalDocuments?: number;
    subtypes?: IncidentsSubtypeRow[];
}

/** Definición de documento requerido (incidents_types_documents) */
interface DocumentoTipologia {
    id: number;
    name: string | null;
    aditionalInfo?: string | null;
    enabled?: number;
    obligatorio?: number;
    prepost?: string | null;
    hadExample?: number | null;
    exampleRoute?: string | null;
}

interface AdjuntoTipologia {
    filename: string;
    base64: string;
    displayName: string;
    /** Fichero original para envío multipart (multer) */
    file: File;
}

const MAX_ADJUNTO_BYTES = 5 * 1024 * 1024;
const MAX_OTRO_ADJUNTO_BYTES = 5 * 1024 * 1024;
/** Si la API no envía optionalDocuments, se mantiene el comportamiento anterior (5). */
const DEFAULT_MAX_OTROS_ADJUNTOS = 5;
const CAP_MAX_OTROS_ADJUNTOS = 100;

@Component({
    selector: "app-newRestricted",
    templateUrl: "./newRestricted.component.html",
    styleUrls: ["./newRestricted.component.scss"],
    standalone: false
})
export class NewRestrictedComponent implements OnInit {
    projectId: number;
    project: any;
    readonly person = signal<any | null>(null);

    full_day = false;
    autorizado = false;
    fi = moment(new Date()).format("YYYY-MM-DDTHH:mm");
    ff = moment(new Date()).format("YYYY-MM-DDTHH:mm");
    motivo = null;
    tipos: Tipo[] = [
        { value: "0", viewValue: "Baja médica" },
        { value: "3", viewValue: "Asistencia a médico" },
        { value: "4", viewValue: "Asistencia a juicio" },
        { value: "5", viewValue: "Asistencia examen oficial" },
        { value: "7", viewValue: "Horas sindicales" },
        { value: "6", viewValue: "Maternidad/Paternidad" },
        { value: "1", viewValue: "Otra" }
    ];
    tipo = this.tipos[0].value;
    /** Compatibilidad con POST actual; la documentación por tipología va en adjuntoPorDocumentoId */
    datosImage = {
        filename: "",
        base64: ""
    };
    errores = [];

    //Solo para el portal empleado Exxita
    readonly agrupaciones = signal<any[]>([]);
    agrupacionSeleccionada = null;

    readonly tiposIncidencias = signal<TipoIncidenciaExxita[]>([]);
    tipoExxitaSeleccionado: number | null = null;
    tipoExxitaSeleccionadoData: any = null;
    /** Subtipos del tipo seleccionado (solo enabled); se recalcula al cambiar el tipo */
    subtiposDisponibles: IncidentsSubtypeRow[] = [];
    subtipoExxitaSeleccionado: number | null = null;

    projectConfig: any;
    comprobacionEnviada = false;
    erroresComprobado = [];
    canSend = false;
    isCheckingAvailability = false;
    isSending = false;

    isSelectorsOK = false;

    /** Solo prepost "A": se pueden adjuntar archivos */
    documentosTipologiaAnteriores: DocumentoTipologia[] = [];
    /** Solo prepost "P": informativo, sin adjuntar */
    documentosTipologiaPosteriores: DocumentoTipologia[] = [];
    /** Adjuntos por id de tipología de documento (incidents_types_documents.id) */
    adjuntoPorDocumentoId: Record<number, AdjuntoTipologia> = {};
    adjuntoErrorPorDocumentoId: Record<number, string> = {};
    isCargandoDocumentos = false;

    /** Archivos libres adicionales (tope según tipo.optionalDocuments, 5 MB c/u) */
    otrosAdjuntosItems: Array<{ file: File; nombreDocumento: string }> = [];
    otrosAdjuntosError: string | null = null;
    readonly maxNombreDocumentoOtrosLength = 250;

    preaviso = 0;

    /** Alta desde módulo Solicitudes internas (?isSolicitud=1): tipos y POST como solicitud. */
    isSolicitudModo = false;

    @Input("modoIncidencia") modoIncidencia: any;

    @Input("agrupacionIncidencia") agrupacionIncidencia: any;
    @Input("tipoIncidencia") tipoIncidencia: any;

    constructor(
        private userSvc: UserService,
        private imagesSvc: ImagesService,
        private projectSvc: ProjectService,
        private route: ActivatedRoute,
        private router: Router,
        private vacacionesSvc: VacacionesService,
        public authSvc: AuthService,
        private dialog: MatDialog,
        private sanitizer: DomSanitizer
    ) {
        this.projectConfig = this.projectSvc.getProjectConfig();
    }
    readonly permisosIncidencias = webPermisosSignal(this.authSvc, "WEB-VACACIONES-INCIDENCIAS");

    ngOnInit(): void {
        if (this.modoIncidencia === "SOLICITUD") {
            this.isSolicitudModo = true;
        }

        this.projectId = parseInt(this.projectSvc.getProjectId());
        this.projectSvc.getProjectConfig().subscribe((data) => {
            this.project = data;
            if (data.portalEmpleadoExxita || data.useRestrictiveAusencias == 1) {
                this.tipo = "9";
                this.loadAgrupacionesTipologia();
            }
        });
        this.userSvc.profile().subscribe((data: ProfileUser) => {
            this.person.set(data.persona);
        });
    }

    private loadAgrupacionesTipologia(): void {
        const exxita = !!this.project?.portalEmpleadoExxita;
        let request$;
        if (this.isSolicitudModo) {
            request$ = exxita
                ? this.vacacionesSvc.getTiposSolicitudesExxita()
                : this.vacacionesSvc.getTiposSolicitudesExxitaNA();
        } else {
            request$ = exxita
                ? this.vacacionesSvc.getTiposIncidenciasExxita()
                : this.vacacionesSvc.getTiposIncidenciasExxitaNA();
        }
        request$.subscribe((data) => this.applyAgrupaciones(data));
    }

    private applyAgrupaciones(data: unknown): void {
        const list = Array.isArray(data) ? data : [];
        this.agrupaciones.set(list);

        if (this.agrupacionIncidencia) {
            this.agrupacionSeleccionada = this.agrupacionIncidencia.agrupacion ?? null;
            this.onAgrupacionChange();
        }

        if (this.tipoIncidencia) {
            this.tipoExxitaSeleccionado = this.tipoIncidencia?.id ? Number(this.tipoIncidencia.id) : null;
            this.onTipoIncidenciaChange();
        }
    }

    onAgrupacionChange() {
        this.isSelectorsOK = false;
        this.documentosTipologiaAnteriores = [];
        this.documentosTipologiaPosteriores = [];
        this.resetAdjuntosTipologia();
        this.resetOtrosAdjuntos();
        this.isCargandoDocumentos = false;
        // Actualizar tiposIncidencias cuando se selecciona una agrupación
        if (this.agrupacionSeleccionada) {
            const agrupacion = this.agrupaciones().find((ag) => ag.agrupacion === this.agrupacionSeleccionada);
            if (agrupacion && agrupacion.lista) {
                this.tiposIncidencias.set(agrupacion.lista);
            } else {
                this.tiposIncidencias.set([]);
            }
        } else {
            this.tiposIncidencias.set([]);
        }

        this.tipoExxitaSeleccionado = null;
        this.subtipoExxitaSeleccionado = null;
        this.subtiposDisponibles = [];
    }

    onTipoIncidenciaChange() {
        this.subtipoExxitaSeleccionado = null;
        this.resetOtrosAdjuntos();
        this.actualizarSubtiposDisponibles();
    }

    private actualizarSubtiposDisponibles() {
        const tipo = this.tiposIncidencias().find((t) => t.id === this.tipoExxitaSeleccionado);
        this.tipoExxitaSeleccionadoData = tipo;
        const rawP = Number(tipo?.["preaviso"]);
        this.preaviso = Number.isFinite(rawP) ? rawP : 0;
        this.ajustarFechasSiPreaviso();
        const raw = tipo?.subtypes;
        if (!Array.isArray(raw) || raw.length === 0) {
            //Sub tipos no disponibles
            this.subtiposDisponibles = [];
            this.getDocumentosDisponiblesTipo(this.tipoExxitaSeleccionado);
            this.isSelectorsOK = true;
            return;
        }
        this.isSelectorsOK = false;
        this.documentosTipologiaAnteriores = [];
        this.documentosTipologiaPosteriores = [];
        this.resetAdjuntosTipologia();
        this.isCargandoDocumentos = false;
        this.subtiposDisponibles = raw.filter((s) => s.enabled === 1 || s.enabled === undefined);
    }
    onSubtipoIncidenciaChange() {
        this.getDocumentosDisponiblesSubtipo(this.tipoExxitaSeleccionado, this.subtipoExxitaSeleccionado);
        this.isSelectorsOK = true;
    }

    abrirDialogInformacionTipoIncidencia(): void {
        const htmlRaw = this.tipoExxitaSeleccionadoData?.aditionalInfo;
        if (!htmlRaw || typeof htmlRaw !== "string") {
            return;
        }
        this.dialog.open(TipoIncidenciaAditionalInfoDialogComponent, {
            width: "70vw",
            maxWidth: "95vw",
            height: "70vh",
            maxHeight: "90vh",
            panelClass: ["panel-feedbalia", "tipo-incidencia-aditional-info-dialog-panel"],
            data: {
                html: this.sanitizer.bypassSecurityTrustHtml(htmlRaw),
                isSolicitud: true
            }
        });
    }

    /** Tercer selector: solo si el tipo tiene subtipos habilitados */
    get muestraSelectorSubtipo(): boolean {
        return this.subtiposDisponibles.length > 0;
    }

    /**
     * `min` para inputs datetime-local cuando preaviso > 0: inicio del día (hoy + preaviso) en hora local.
     */
    get minFechaHoraConPreaviso(): string | null {
        const d = Number(this.preaviso);
        if (!Number.isFinite(d) || d <= 0) {
            return null;
        }
        return moment().startOf("day").add(d, "days").format("YYYY-MM-DDTHH:mm");
    }

    /** Inicio y fin no pueden quedar por debajo del mínimo por preaviso. */
    fechasCumplenPreaviso(): boolean {
        const minStr = this.minFechaHoraConPreaviso;
        if (minStr == null) {
            return true;
        }
        const minMs = new Date(minStr).getTime();
        if (!this.fi || !this.ff) {
            return false;
        }
        return new Date(this.fi).getTime() >= minMs && new Date(this.ff).getTime() >= minMs;
    }

    private ajustarFechasSiPreaviso(): void {
        const minStr = this.minFechaHoraConPreaviso;
        if (minStr == null) {
            return;
        }
        const minM = moment(minStr, "YYYY-MM-DDTHH:mm", true);
        if (!minM.isValid()) {
            return;
        }
        if (this.fi && moment(this.fi).isBefore(minM)) {
            this.fi = minStr;
        }
        if (this.ff && moment(this.ff).isBefore(minM)) {
            this.ff = minStr;
        }
        if (this.fi && this.ff && moment(this.ff).isBefore(moment(this.fi))) {
            this.ff = this.fi;
        }
    }

    canSelectTipoIncidencia(): boolean {
        return !!this.agrupacionSeleccionada && this.tiposIncidencias().length > 0;
    }

    getImageUser(nickname) {
        return this.imagesSvc.getImagePerson(nickname, this.projectId);
    }

    esDocObligatorio(doc: DocumentoTipologia): boolean {
        return Number(doc.obligatorio) === 1;
    }

    getAdjunto(docId: number): AdjuntoTipologia | null {
        return this.adjuntoPorDocumentoId[docId] ?? null;
    }

    documentosObligatoriosCompletos(): boolean {
        const obligatorios = this.documentosTipologiaAnteriores.filter((d) => this.esDocObligatorio(d));
        if (obligatorios.length === 0) {
            return true;
        }
        return obligatorios.every((d) => !!this.adjuntoPorDocumentoId[d.id]);
    }

    private resetAdjuntosTipologia(): void {
        this.adjuntoPorDocumentoId = {};
        this.adjuntoErrorPorDocumentoId = {};
        this.datosImage = { filename: "", base64: "" };
    }

    private resetOtrosAdjuntos(): void {
        this.otrosAdjuntosItems = [];
        this.otrosAdjuntosError = null;
    }

    /**
     * Límite según el tipo seleccionado (incidents_types.optionalDocuments).
     * 0 = sin otros adjuntos. Si el campo no viene en la API, se usa el valor por defecto.
     */
    get maxOtrosAdjuntosPermitidos(): number {
        const tipo = this.tiposIncidencias().find((t) => t.id === this.tipoExxitaSeleccionado);
        if (!tipo) {
            return 0;
        }
        const raw = tipo.optionalDocuments ?? (tipo as unknown as Record<string, unknown>)["optional_documents"];
        if (raw === undefined || raw === null) {
            return DEFAULT_MAX_OTROS_ADJUNTOS;
        }
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
            return 0;
        }
        return Math.min(Math.floor(n), CAP_MAX_OTROS_ADJUNTOS);
    }

    muestraSeccionOtrosAdjuntos(): boolean {
        return this.maxOtrosAdjuntosPermitidos > 0;
    }

    puedeAnadirMasOtrosAdjuntos(): boolean {
        const max = this.maxOtrosAdjuntosPermitidos;
        return max > 0 && !this.canSend && this.otrosAdjuntosItems.length < max;
    }

    onOtrosAdjuntosSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }
        this.otrosAdjuntosError = null;
        const max = this.maxOtrosAdjuntosPermitidos;
        if (max <= 0) {
            input.value = "";
            return;
        }
        const files = Array.from(input.files);
        const mensajes: string[] = [];
        let next = [...this.otrosAdjuntosItems];

        for (const file of files) {
            if (next.length >= max) {
                mensajes.push(`Solo se permiten hasta ${max} archivos adicionales.`);
                break;
            }
            if (file.size > MAX_OTRO_ADJUNTO_BYTES) {
                mensajes.push(`«${file.name}» supera 5 MB.`);
                continue;
            }
            const duplicado = next.some((x) => x.file.name === file.name && x.file.size === file.size);
            if (duplicado) {
                continue;
            }
            const nombreInicial =
                file.name.length > this.maxNombreDocumentoOtrosLength
                    ? file.name.substring(0, this.maxNombreDocumentoOtrosLength)
                    : file.name;
            next.push({ file, nombreDocumento: nombreInicial });
        }

        this.otrosAdjuntosItems = next;
        if (mensajes.length > 0) {
            this.otrosAdjuntosError = mensajes.join(" ");
        }
        input.value = "";
    }

    removeOtroAdjunto(index: number): void {
        this.otrosAdjuntosItems.splice(index, 1);
        this.otrosAdjuntosError = null;
    }

    getTotalOtrosAdjuntosSize(): number {
        return this.otrosAdjuntosItems.reduce((s, x) => s + (x.file?.size ?? 0), 0);
    }

    formatOtroFileSize(bytes: number): string {
        if (bytes === 0) {
            return "0 B";
        }
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    }

    getOtroFileIcon(file: File): string {
        const type = file.type;
        if (type.startsWith("image/")) {
            return "🖼️";
        }
        if (type.startsWith("video/")) {
            return "🎥";
        }
        if (type.startsWith("audio/")) {
            return "🎵";
        }
        if (type === "application/pdf") {
            return "📄";
        }
        return "📎";
    }

    private normalizarListaDocumentos(arr: DocumentoTipologia[] | undefined): DocumentoTipologia[] {
        const byId = new Map<number, DocumentoTipologia>();
        for (const d of arr || []) {
            if (d?.id == null) continue;
            if (d.enabled === 0) continue;
            byId.set(Number(d.id), d);
        }
        return Array.from(byId.values());
    }

    private aplicarListasDocumentosDesdeApi(data: {
        anteriores?: DocumentoTipologia[];
        posteriores?: DocumentoTipologia[];
    }): void {
        this.documentosTipologiaAnteriores = this.normalizarListaDocumentos(data.anteriores);
        this.documentosTipologiaPosteriores = this.normalizarListaDocumentos(data.posteriores);
    }

    private getExtensionFromFileName(name: string): string {
        const parts = name.split(".");
        return parts.length > 1 ? parts[parts.length - 1] : "bin";
    }

    onAdjuntoTipologiaChange(event: Event, doc: DocumentoTipologia): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        delete this.adjuntoErrorPorDocumentoId[doc.id];
        if (!file) {
            return;
        }
        if (file.size > MAX_ADJUNTO_BYTES) {
            this.adjuntoErrorPorDocumentoId[doc.id] = "El archivo no puede superar 5 MB.";
            input.value = "";
            return;
        }
        const ext = this.getExtensionFromFileName(file.name);
        const filename = this.getFilenameForTipologia(doc.id, ext);
        const reader = new FileReader();
        reader.onload = () => {
            this.adjuntoPorDocumentoId[doc.id] = {
                filename,
                base64: String(reader.result),
                displayName: file.name,
                file
            };
            delete this.adjuntoErrorPorDocumentoId[doc.id];
        };
        reader.onerror = () => {
            this.adjuntoErrorPorDocumentoId[doc.id] = "No se ha podido leer el archivo.";
            input.value = "";
        };
        reader.readAsDataURL(file);
        input.value = "";
    }

    descargarEjemploDocumento(doc: DocumentoTipologia): void {
        doc["bloqueadaDescarga"] = true;
        this.vacacionesSvc.descargarPlantillaSolicitud(doc.id).subscribe(
            (data) => {
                const file = new Blob([data], { type: "application/pdf" });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL, "_blank");
                doc["bloqueadaDescarga"] = false;
            },
            (error) => {
                console.error("Error al descargar la plantilla:", error);
            }
        );
    }

    quitarAdjuntoTipologia(docId: number, input?: HTMLInputElement): void {
        delete this.adjuntoPorDocumentoId[docId];
        delete this.adjuntoErrorPorDocumentoId[docId];
        if (input) {
            input.value = "";
        }
    }

    private getFilenameForTipologia(documentoTipologiaId: number, ext: string): string {
        return (
            "user" +
            this.person()!.id +
            "_inc_tipol" +
            documentoTipologiaId +
            "_" +
            moment().format("YYYYMMDDHHmmssSSSS") +
            "." +
            ext
        );
    }

    send() {
        // Prevenir envíos múltiples
        if (this.isSending) {
            return;
        }

        this.errores = [];
        this.isSending = true;

        /** Una sola lista: id_documento numérico = tipología; null = adicional. Mismo orden que cada parte `archivos` del FormData (multer .array("archivos")). */
        const documentos: Array<{ id_documento: number | null; nombre: string; isObligatorio: number }> = [];
        const archivos: File[] = [];

        for (const doc of this.documentosTipologiaAnteriores) {
            const adj = this.adjuntoPorDocumentoId[doc.id];
            if (adj?.file) {
                documentos.push({
                    id_documento: doc.id,
                    nombre: (doc.name || adj.displayName || "").trim() || adj.file.name,
                    isObligatorio: doc.obligatorio ? 1 : 0
                });
                archivos.push(adj.file);
            }
        }
        for (const item of this.otrosAdjuntosItems) {
            const nombre = (item.nombreDocumento || "").trim() || item.file.name;
            documentos.push({ id_documento: null, nombre, isObligatorio: 0 });
            archivos.push(item.file);
        }

        const formData = new FormData();
        formData.append("id_persona", String(this.person()!.id));
        formData.append("tipo", String(this.tipo));
        formData.append("dateIni", this.fi.split("T")[0]);
        formData.append("dateFin", this.ff.split("T")[0]);
        formData.append("horaIni", this.fi.split("T")[1]);
        formData.append("horaFin", this.ff.split("T")[1]);
        formData.append("allow", String(this.autorizado ? 1 : 0));
        formData.append("dia_completo", String(this.full_day ? 1 : 0));
        formData.append("motivo", this.motivo ?? "");
        if (this.isSolicitudModo) {
            formData.append("isSolicitud", "1");
        }
        if (this.project.portalEmpleadoExxita && this.tipoExxitaSeleccionado != null) {
            formData.append("extra_type", String(this.tipoExxitaSeleccionado));
        }
        if (
            this.project.portalEmpleadoExxita &&
            this.muestraSelectorSubtipo &&
            this.subtipoExxitaSeleccionado != null
        ) {
            formData.append("id_subtipo", String(this.subtipoExxitaSeleccionado));
        }
        formData.append("documentos", JSON.stringify(documentos));
        for (const archivo of archivos) {
            formData.append("archivos", archivo, archivo.name);
        }

        if (this.motivo && new Date(this.fi) <= new Date(this.ff) && this.fechasCumplenPreaviso()) {
            this.vacacionesSvc.postIncidenciaWithDocuments(formData).subscribe(
                (data) => {
                    this.isSending = false;
                    this.router.navigate(this.isSolicitudModo ? ["/solicitudes"] : ["/vacaciones/user"]);
                },
                (error) => {
                    this.isSending = false;
                    this.errores.push("Error al enviar la incidencia. Por favor, inténtelo de nuevo.");
                }
            );
        } else {
            if (!this.motivo) {
                this.errores.push("* Se ha de incluir un motivo");
            }
            if (new Date(this.fi) > new Date(this.ff)) {
                this.errores.push("* La fecha de inicio no puede ser menor que la de fin");
            }
            if (!this.fi) {
                this.errores.push("* La fecha de inicio ha de estar definida");
            }
            if (!this.ff) {
                this.errores.push("* La fecha de fin ha de estar definida");
            }
            if (!this.fechasCumplenPreaviso()) {
                const minStr = this.minFechaHoraConPreaviso;
                const fechaTxt = minStr ? moment(minStr, "YYYY-MM-DDTHH:mm").format("DD/MM/YYYY") : "";
                this.errores.push(
                    `* Este tipo exige un preaviso de ${this.preaviso} día(s): inicio y fin deben ser el ${fechaTxt} o posteriores.`
                );
            }
            this.isSending = false;
        }
    }
    validarDatosExxita(): boolean {
        const subtipoRequeridoOk = !this.muestraSelectorSubtipo || this.subtipoExxitaSeleccionado != null;
        return (
            !!this.agrupacionSeleccionada &&
            this.tipoExxitaSeleccionado != null &&
            subtipoRequeridoOk &&
            !!this.motivo &&
            new Date(this.fi) < new Date(this.ff) &&
            this.fechasCumplenPreaviso() &&
            !this.isCargandoDocumentos &&
            this.documentosObligatoriosCompletos()
        );
    }

    cancelarComprobacion(): void {
        this.comprobacionEnviada = false;
        this.canSend = false;
        this.erroresComprobado = [];
        this.isCheckingAvailability = false;
        this.isSending = false;
    }

    comprobarIncidencias() {
        this.errores = [];
        let body = {
            id_persona: this.person()!.id,
            tipo: this.tipo,
            dateIni: this.fi.split("T")[0],
            dateFin: this.ff.split("T")[0],
            horaIni: this.fi.split("T")[1],
            horaFin: this.ff.split("T")[1],
            //'ruta':this.ruta,
            allow: this.autorizado ? 1 : 0,
            dia_completo: this.full_day ? 1 : 0,
            motivo: this.motivo,
            image: this.datosImage,
            extra_type: this.project.portalEmpleadoExxita ? this.tipoExxitaSeleccionado : null,
            id_subtipo:
                this.project.portalEmpleadoExxita && this.muestraSelectorSubtipo ? this.subtipoExxitaSeleccionado : null
        };

        if (this.motivo && new Date(this.fi) <= new Date(this.ff) && this.fechasCumplenPreaviso()) {
            this.isCheckingAvailability = true;

            this.vacacionesSvc.comprobarDisponibilidadIncidencia(body).subscribe(
                (data) => {
                    this.isCheckingAvailability = false;
                    this.comprobacionEnviada = true;
                    if (data.isOk) {
                        this.canSend = true;
                    } else {
                        this.erroresComprobado = data.errores;
                        this.canSend = false;
                    }
                },
                (error) => {
                    this.isCheckingAvailability = false;
                    this.errores.push("Error al comprobar la disponibilidad. Por favor, inténtelo de nuevo.");
                }
            );
        } else {
            if (!this.motivo) {
                this.errores.push("* Se ha de incluir un motivo");
            }
            if (new Date(this.fi) > new Date(this.ff)) {
                this.errores.push("* La fecha de inicio no puede ser menor que la de fin");
            }
            if (!this.fi) {
                this.errores.push("* La fecha de inicio ha de estar definida");
            }
            if (!this.ff) {
                this.errores.push("* La fecha de fin ha de estar definida");
            }
            if (!this.fechasCumplenPreaviso()) {
                const minStr = this.minFechaHoraConPreaviso;
                const fechaTxt = minStr ? moment(minStr, "YYYY-MM-DDTHH:mm").format("DD/MM/YYYY") : "";
                this.errores.push(
                    `* Este tipo exige un preaviso de ${this.preaviso} día(s): inicio y fin deben ser el ${fechaTxt} o posteriores.`
                );
            }
        }
    }

    getDocumentosDisponiblesTipo(tipoId: any): void {
        this.isCargandoDocumentos = true;
        this.documentosTipologiaAnteriores = [];
        this.documentosTipologiaPosteriores = [];
        this.resetAdjuntosTipologia();
        this.resetOtrosAdjuntos();
        this.vacacionesSvc.getAllDocuments(tipoId, null).subscribe({
            next: (data) => {
                this.aplicarListasDocumentosDesdeApi(data);
                this.isCargandoDocumentos = false;
            },
            error: () => {
                this.isCargandoDocumentos = false;
            }
        });
    }

    getDocumentosDisponiblesSubtipo(tipoId: any, subtipoId: any): void {
        this.isCargandoDocumentos = true;
        this.documentosTipologiaAnteriores = [];
        this.documentosTipologiaPosteriores = [];
        this.resetAdjuntosTipologia();
        this.resetOtrosAdjuntos();
        this.vacacionesSvc.getAllDocuments(tipoId, subtipoId).subscribe({
            next: (data) => {
                this.aplicarListasDocumentosDesdeApi(data);
                this.isCargandoDocumentos = false;
            },
            error: () => {
                this.isCargandoDocumentos = false;
            }
        });
    }

    comprobarFechas(): boolean {
        return new Date(this.fi) < new Date(this.ff);
    }
}
